import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { createElement } from "react";
import { toast } from "react-toastify";
import ForegroundNotificationToast from "../components/ForegroundNotificationToast";
import { unregisterFirebaseMessagingServiceWorker } from "../firebase/firebase.helper";

export type NotificationPlatform = "web" | "mobile";

const getPlatform = (): NotificationPlatform =>
  Capacitor.isNativePlatform() ? "mobile" : "web";

const NATIVE_PERMISSION_KEY = "receive";
let nativeListenersRegistered = false;

type NotificationDisplayData = {
  title?: string | null;
  body?: string | null;
  image?: string | null;
  imageUrl?: string | null;
  gender?: string | null;
  badge?: string | null;
  url?: string | null;
};

const NEW_PROFILE_NOTIFICATION_TITLE =
  "New profile created by Matrimony studio";

const cleanNotificationValue = (value?: string | null) => {
  if (!value) {
    return "";
  }

  const text = value.trim();
  if (!text) {
    return "";
  }

  const normalized = text.toLowerCase();
  if (normalized === "null" || normalized === "undefined") {
    return "";
  }

  return text;
};

const formatNotificationTime = (value?: string | null) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const openNotificationUrl = (url?: string | null) => {
  const value = cleanNotificationValue(url);

  if (!value || typeof window === "undefined") {
    return;
  }

  try {
    const parsed = new URL(value, window.location.origin);

    const isMatrimonyStudioUrl =
      parsed.hostname === "matrimonystudio.in" ||
      parsed.hostname === "www.matrimonystudio.in";

    if (isMatrimonyStudioUrl) {
      window.location.assign(
        `${parsed.pathname}${parsed.search}${parsed.hash}`,
      );
      return;
    }

    if (/^https?:\/\//i.test(value)) {
      window.open(value, "_blank", "noopener,noreferrer");
      return;
    }
  } catch {
    // Relative app route.
  }

  window.location.assign(value.startsWith("/") ? value : `/${value}`);
};

const showToastForNotification = ({
  title,
  body,
  image,
  imageUrl,
  gender,
  badge,
  url,
}: NotificationDisplayData) => {
  const content = body || title;
  if (!content) {
    return;
  }

  const toastId = `notification-${title || "notification"}-${body || ""}-${url || ""}`;
  toast(
    createElement(ForegroundNotificationToast, {
      title,
      body,
      image,
      imageUrl,
      gender,
      badge,
      onOpen: () => openNotificationUrl(url),
    }),
    {
      toastId,
      autoClose: 5000,
      closeOnClick: false,
      icon: false,
      className: "!bg-transparent !shadow-none !p-0",
      // @ts-ignore
      bodyClassName: "!p-0",
      progressClassName:
        "!bg-gradient-to-r !from-fuchsia-500 !via-rose-500 !to-violet-500",
    },
  );
};

const buildNotificationDisplayData = (
  title?: string | null,
  body?: string | null,
  data?: Record<string, any> | null,
): NotificationDisplayData => {
  if (data?.type === "NEW_PROFILE_ADDED") {
    const bodyParts = [
      cleanNotificationValue(data?.fullName),
      cleanNotificationValue(data?.caste),
      cleanNotificationValue(data?.annualIncome),
      cleanNotificationValue(data?.citizenship),
      cleanNotificationValue(data?.district),
    ].filter(Boolean);

    return {
      title: NEW_PROFILE_NOTIFICATION_TITLE,
      body: bodyParts.join(" | ") || cleanNotificationValue(body) || null,
      image: data?.image || null,
      imageUrl: data?.imageUrl || null,
      gender: data?.gender || null,
      badge: formatNotificationTime(data?.profileCreatedAt) || null,
      url: data?.url || data?.link || null,
    };
  }

  const profileName = data?.fullName || title || "New profile";
  return {
    title: profileName,
    body: body || data?.body || null,
    image: data?.image || null,
    imageUrl: data?.imageUrl || null,
    gender: data?.gender || null,
    badge: data?.isOtherBureau ? "Other bureau" : "New profile",
    url: data?.url || data?.link || null,
  };
};

const attachNativeNotificationListeners = async () => {
  if (nativeListenersRegistered) {
    console.log("[Push] listeners already registered");

    return;
  }

  console.log("[Push] registering native listeners");

  try {
    await PushNotifications.addListener(
      "pushNotificationReceived",

      (notification) => {
        console.log("[Push] notification received", notification);

        const title =
          notification.title ?? notification.data?.title ?? "Notification";

        const body = notification.body ?? notification.data?.body ?? null;

        showToastForNotification(
          buildNotificationDisplayData(
            title,

            body,

            notification.data ?? null,
          ),
        );
      },
    );

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",

      ({ notification, actionId }) => {
        const data = notification?.data ?? {};

        console.log("[Push] notification clicked", {
          actionId,

          data,

          notification,
        });

        const martialId =
          cleanNotificationValue(data.martialId) ||
          cleanNotificationValue(data.senderMartialId);

        const userId =
          cleanNotificationValue(data.userId) ||
          cleanNotificationValue(data.senderUserId);

        const profileIdentifier = martialId || userId;

        if (!profileIdentifier) {
          console.warn("[Push] profile identifier missing", data);

          return;
        }

        const targetUrl = `/admin/profile/detail/${encodeURIComponent(
          profileIdentifier,
        )}`;

        console.log("[Push] navigating to", targetUrl);

        openNotificationUrl(targetUrl);
      },
    );

    nativeListenersRegistered = true;

    console.log("[Push] listeners registered successfully");
  } catch (error) {
    nativeListenersRegistered = false;

    console.error("[Push] listener registration failed", error);
  }
};

const ensureNativePermission = async () => {
  const permission = await PushNotifications.checkPermissions();
  const currentStatus = permission[NATIVE_PERMISSION_KEY];

  if (currentStatus === "granted") {
    return true;
  }

  const requested = await PushNotifications.requestPermissions();
  return requested[NATIVE_PERMISSION_KEY] === "granted";
};

const waitForNativeToken = async () =>
  new Promise<string>(async (resolve, reject) => {
    let cleanedUp = false;
    let registrationHandle: PluginListenerHandle | undefined;
    let errorHandle: PluginListenerHandle | undefined;

    const cleanup = async () => {
      if (cleanedUp) {
        return;
      }
      cleanedUp = true;

      await registrationHandle?.remove();
      await errorHandle?.remove();
    };

    try {
      registrationHandle = await PushNotifications.addListener(
        "registration",
        async (token) => {
          await cleanup();
          resolve(token.value);
        },
      );

      errorHandle = await PushNotifications.addListener(
        "registrationError",
        async (error) => {
          await cleanup();
          reject(error);
        },
      );

      await PushNotifications.register();
    } catch (error) {
      await cleanup();
      reject(error);
    }
  });

const getNativeFcmToken = async () => {
  try {
    const hasPermission = await ensureNativePermission();
    if (!hasPermission) {
      console.warn("Native push notification permission denied");
      return null;
    }

    const token = await waitForNativeToken();
    await attachNativeNotificationListeners();
    return token;
  } catch (error) {
    console.error("Native push registration failed", error);
    return null;
  }
};

export const getFcmToken = async () => {
  if (Capacitor.isNativePlatform()) {
    return getNativeFcmToken();
  }

  await unregisterFirebaseMessagingServiceWorker();
  return null;
};

export const initializeNotificationListeners = () => {
  if (Capacitor.isNativePlatform()) {
    attachNativeNotificationListeners().catch((error) =>
      console.error("Failed to attach native push listeners", error),
    );
    return;
  }

  unregisterFirebaseMessagingServiceWorker().catch((error) =>
    console.error("Failed to unregister web push service worker", error),
  );
};

export const getNotificationPlatform = (): NotificationPlatform =>
  getPlatform();
